// api.js — Secure API client with silent token refresh & exponential backoff

import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { compressImage } from './utils/compressImage';
import { API_BASE_URL } from './apiConfig';
import { isBranchScopedRequest } from './utils/requestScope';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track refresh state to prevent concurrent refresh loops
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor — attach JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const selectedBranchId = localStorage.getItem('selectedBranchId');
    if (selectedBranchId && isBranchScopedRequest(config.url)) config.headers['X-Branch-ID'] = selectedBranchId;
    // Let browser set Content-Type for FormData (multipart boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      config.timeout = 60000;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — silent token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 402 && error.response.data?.code === 'SUBSCRIPTION_INACTIVE') {
      window.dispatchEvent(new CustomEvent('hosteldesk-subscription-inactive', { detail: error.response.data.subscriptionAccess }));
    }

    // Only attempt refresh on 401, not on auth endpoints, and not already retried
    if (
      error.response?.status === 401 &&
      error.response?.data?.code !== 'ACCOUNT_DISABLED' &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/users/auth') &&
      !originalRequest.url?.includes('/users/password-auth') &&
      !originalRequest.url?.includes('/user/link-signin')
    ) {
      originalRequest._retry = true;
      if (isRefreshing) {
        // Queue this request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      isRefreshing = true;
      const sessionAtStart = localStorage.getItem('accessToken');

      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;

        if (!currentUser) {
          throw new Error('No authenticated user');
        }

        // Force refresh the Firebase ID token
        const idToken = await currentUser.getIdToken(true);

        // Re-authenticate with backend using fresh Firebase token
        const response = await axios.post(`${API_BASE_URL}/users/auth`, {
          idToken,
          email: currentUser.email,
          displayName: currentUser.displayName,
        }, { timeout: 15000 });

        const { accessToken } = response.data;
        if (!accessToken || localStorage.getItem('accessToken') !== sessionAtStart) throw new Error('Session ended or changed during refresh');
        localStorage.setItem('accessToken', accessToken);

        processQueue(null, accessToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Clear token and redirect to sign-in
        if (localStorage.getItem('accessToken') === sessionAtStart && (refreshError.response?.status === 401 || refreshError.response?.status === 403 || !getAuth().currentUser)) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('selectedBranchId');
          window.location.hash = '#/signin';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// =================================================================
// Auto-compress images in FormData before upload
// =================================================================

const compressFormDataImages = async (formData) => {
  const compressed = new FormData();
  for (const [key, value] of formData.entries()) {
    if (value instanceof File && value.type.startsWith('image/')) {
      compressed.append(key, await compressImage(value));
    } else {
      compressed.append(key, value);
    }
  }
  return compressed;
};

// =================================================================
// secureAPI — Drop-in replacement for per-page SecureAPI classes
// Returns response.data directly (not axios response wrapper)
// =================================================================

export const secureAPI = {
  setToken(token) {
    if (token) {
      localStorage.setItem('accessToken', token);
    } else {
      localStorage.removeItem('accessToken');
    }
  },
  async authenticateUser(email, displayName) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');
    const idToken = await user.getIdToken(true);
    const res = await api.post('/users/auth', { idToken, email, displayName });
    if (res.data.accessToken) {
      localStorage.setItem('accessToken', res.data.accessToken);
    }
    return res.data;
  },
  async get(endpoint) {
    const res = await api.get(endpoint);
    return res.data;
  },
  async post(endpoint, data) {
    const res = await api.post(endpoint, data);
    return res.data;
  },
  async put(endpoint, data) {
    const res = await api.put(endpoint, data);
    return res.data;
  },
  async patch(endpoint, data) {
    const res = await api.patch(endpoint, data);
    return res.data;
  },
  async delete(endpoint, data) {
    const res = await api.delete(endpoint, data === undefined ? undefined : { data });
    return res.data;
  },
  async postFormData(endpoint, formData) {
    const compressed = await compressFormDataImages(formData);
    const res = await api.post(endpoint, compressed);
    return res.data;
  },
  async putFormData(endpoint, formData) {
    const compressed = await compressFormDataImages(formData);
    const res = await api.put(endpoint, compressed);
    return res.data;
  },
};

export default api;
